import { ReactElement, useState, useRef } from 'react'
import { useFormContext } from 'react-hook-form'
import { TabContext, TabList, TabPanel } from '@mui/lab'
import { Grid, Divider, Switch, FormControlLabel, Box } from '@mui/material'
import { usageClassOptions } from '@editor/constants'
import {
  SelectLoadable,
  Select,
  TextField,
  MillimeterField,
  FormTab,
  CentimeterField,
} from '@ui/forms'
import { getSoftWoodMaterials, getGluelamMaterials, getLvlMaterials } from '@queries'
import { remapMaterialIdentifier } from 'src/components/pages/Editor/components/SingleElementCSForm/components/FormFields'
import { materialTypeOptions } from '../constants'
import { tabConfig } from './constants'

const getMaterialQueryByType = (materialType: string) => {
  switch (materialType) {
    case 'softwoodMaterial':
      return getSoftWoodMaterials
    case 'glulamMaterial':
      return getGluelamMaterials
    default:
      return getLvlMaterials
  }
}

interface Props {
  variant: TimberFrameLayerVariant
}

const TimberFrameLayerFormFields = ({ variant }: Props): ReactElement => {
  const { watch, setValue, getValues } = useFormContext()
  const hasInsulation = watch('layer.hasInsulation')
  const materialTypeStandardField =
    variant === 'wall' ? 'materialTypeStandardRip' : 'materialTypeStandardBeam'
  const materialTypeStandardWatch = watch(materialTypeStandardField) || 'softwoodMaterial'
  const materialTypeLintelWatch =
    variant === 'wall' ? watch('materialTypeLintel') || 'softwoodMaterial' : undefined
  const materialTypeTopChordWatch =
    variant === 'wall' ? watch('materialTypeTopChord') || 'softwoodMaterial' : undefined
  const materialTypeBottomChordWatch =
    variant === 'wall' ? watch('materialTypeBottomChord') || 'softwoodMaterial' : undefined

  const [tab, setTab] = useState<keyof typeof tabConfig>('standard')
  const savedInsulation = useRef<ProductLayer | null>(null)

  const handleInsulationToggle = (event: React.ChangeEvent<HTMLInputElement>) => {
    const checked = event.target.checked
    setValue('layer.hasInsulation', checked)

    if (checked) {
      // Restore saved values when turning insulation on
      if (savedInsulation.current) {
        setValue('layer.insulation.name', savedInsulation.current.product.name)
        setValue('layer.insulation.density', savedInsulation.current.product.density)
        setValue('layer.insulation_thickness', savedInsulation.current.thickness)
      } else {
        // just assume something
        setValue('layer.insulation.name', 'Isolierung')
        setValue('layer.insulation.density', 1)
        setValue('layer.insulation_thickness', 1.0e-3)
      }
    } else if (!checked) {
      // Save current values before turning insulation off
      savedInsulation.current = {
        product: {
          name: getValues('layer.insulation.name'),
          density: getValues('layer.insulation.density'),
        },
        thickness: getValues('layer.insulation_thickness'),
        kind: 'product-layer',
      }

      // Clear form values
      setValue('layer.insulation', null)
      setValue('layer.insulation_thickness', null)
    }
  }

  const renderWallFields = () => (
    <TabContext value={tab}>
      <Box
        sx={{
          borderBottom: 1,
          borderColor: 'divider',
          '& .MuiTab-root': {
            fontSize: 12,
            paddingX: ({ spacing }) => spacing(1.5),
          },
        }}
      >
        <TabList onChange={(_, value) => setTab(value)}>
          <FormTab
            {...tabConfig.standard}
            fields="layer.crossSectionStandardRip"
            data-cy="tab-standard-rip"
          />
          <FormTab {...tabConfig.lintel} fields="layer.crossSectionLintel" data-cy="tab-lintel" />
          <FormTab
            {...tabConfig.topChord}
            fields="layer.crossSectionTopChord"
            data-cy="tab-top-chord"
          />
          <FormTab
            {...tabConfig.bottomChord}
            fields="layer.crossSectionBottomChord"
            data-cy="tab-bottom-chord"
          />
        </TabList>
      </Box>

      <TabPanel value={tabConfig.standard.value} sx={{ margin: 0 }}>
        <Grid container spacing={2} marginX={-2}>
          <Grid item xs={12}>
            <Select
              label="Standard Rippe Materialtyp"
              name="materialTypeStandardRip"
              options={materialTypeOptions}
              data-cy="select-standard-rip"
            />
          </Grid>
          <Grid item xs={12}>
            <SelectLoadable
              label="Standard Rippe Material"
              name="layer.crossSectionStandardRip.material"
              placeholder="Material auswählen"
              query={getMaterialQueryByType(materialTypeStandardWatch)}
              renderOptions={(data: Material[] = []) =>
                data.map(material => ({
                  value: material,
                  label: remapMaterialIdentifier(material.identifier),
                }))
              }
              data-cy="crosssection-standard-material"
            />
          </Grid>
          <Grid item xs={6}>
            <CentimeterField
              label="Querschnittbreite"
              name="layer.crossSectionStandardRip.shape.width"
              data-cy="crosssection-standard-shape-width"
              twoDecimalPlaces
            />
          </Grid>
          <Grid item xs={6}>
            <CentimeterField
              tooltip={{
                text: 'Bestimmt die Dicke der Schicht',
              }}
              label="Querschnitthöhe"
              name="layer.crossSectionStandardRip.shape.height"
              data-cy="crosssection-standard-shape-height"
              twoDecimalPlaces
            />
          </Grid>
          <Grid item xs={6}>
            <CentimeterField label="Standard Rippe Abstand" name="layer.rip_step_size" />
          </Grid>
          <Grid item xs={6}>
            <Select
              label="Nutzungsklasse"
              name="layer.crossSectionStandardRip.usage_class"
              options={usageClassOptions}
              data-cy="crosssection-standard-usage-class"
            />
          </Grid>
        </Grid>
      </TabPanel>

      <TabPanel value={tabConfig.lintel.value} sx={{ margin: 0 }}>
        <Grid container spacing={2} marginX={-2}>
          <Grid item xs={12}>
            <Select
              label="Fenstersturz Materialtyp"
              name="materialTypeLintel"
              options={materialTypeOptions}
              data-cy="select-lintel"
            />
          </Grid>
          <Grid item xs={12}>
            <SelectLoadable
              label="Fenstersturz Material"
              name="layer.crossSectionLintel.material"
              placeholder="Material auswählen"
              query={getMaterialQueryByType(materialTypeLintelWatch || 'softwoodMaterial')}
              renderOptions={(data: Material[] = []) =>
                data.map(material => ({
                  value: material,
                  label: remapMaterialIdentifier(material.identifier),
                }))
              }
              data-cy="crosssection-lintel-material"
            />
          </Grid>
          <Grid item xs={6}>
            <CentimeterField
              tooltip={{
                text: 'Bestimmt die Dicke der Schicht',
              }}
              label="Querschnittbreite"
              name="layer.crossSectionLintel.shape.width"
              data-cy="crosssection-lintel-shape-width"
              twoDecimalPlaces
            />
          </Grid>
          <Grid item xs={6}>
            <CentimeterField
              label="Querschnitthöhe"
              name="layer.crossSectionLintel.shape.height"
              data-cy="crosssection-lintel-shape-height"
              twoDecimalPlaces
            />
          </Grid>
          <Grid item xs={6}>
            <Select
              label="Nutzungsklasse"
              name="layer.crossSectionLintel.usage_class"
              options={usageClassOptions}
              data-cy="crosssection-lintel-usage-class"
            />
          </Grid>
        </Grid>
      </TabPanel>

      <TabPanel value={tabConfig.topChord.value} sx={{ margin: 0 }}>
        <Grid container spacing={2} marginX={-2}>
          <Grid item xs={12}>
            <Select
              label="Rähm Materialtyp"
              name="materialTypeTopChord"
              options={materialTypeOptions}
              data-cy="select-top-chord"
            />
          </Grid>
          <Grid item xs={12}>
            <SelectLoadable
              label="Rähm Material"
              name="layer.crossSectionTopChord.material"
              placeholder="Material auswählen"
              query={getMaterialQueryByType(materialTypeTopChordWatch || 'softwoodMaterial')}
              renderOptions={(data: Material[] = []) =>
                data.map(material => ({
                  value: material,
                  label: remapMaterialIdentifier(material.identifier),
                }))
              }
              data-cy="crosssection-top-chord-material"
            />
          </Grid>
          <Grid item xs={6}>
            <CentimeterField
              tooltip={{
                text: 'Bestimmt die Dicke der Schicht',
              }}
              label="Rähm Breite"
              name="layer.crossSectionTopChord.shape.width"
              data-cy="crosssection-top-chord-shape-width"
              twoDecimalPlaces
            />
          </Grid>
          <Grid item xs={6}>
            <CentimeterField
              label="Querschnitthöhe"
              name="layer.crossSectionTopChord.shape.height"
              data-cy="crosssection-top-chord-shape-height"
              twoDecimalPlaces
            />
          </Grid>
          <Grid item xs={6}>
            <Select
              label="Nutzungsklasse"
              name="layer.crossSectionTopChord.usage_class"
              options={usageClassOptions}
              data-cy="crosssection-top-chord-usage-class"
            />
          </Grid>
        </Grid>
      </TabPanel>

      <TabPanel value={tabConfig.bottomChord.value} sx={{ margin: 0 }}>
        <Grid container spacing={2} marginX={-2}>
          <Grid item xs={12}>
            <Select
              label="Schwelle Materialtyp"
              name="materialTypeBottomChord"
              options={materialTypeOptions}
              data-cy="select-bottom-chord"
            />
          </Grid>
          <Grid item xs={12}>
            <SelectLoadable
              label="Schwelle Material"
              name="layer.crossSectionBottomChord.material"
              placeholder="Material auswählen"
              query={getMaterialQueryByType(materialTypeBottomChordWatch || 'softwoodMaterial')}
              renderOptions={(data: Material[] = []) =>
                data.map(material => ({
                  value: material,
                  label: remapMaterialIdentifier(material.identifier),
                }))
              }
              data-cy="crosssection-bottom-chord-material"
            />
          </Grid>
          <Grid item xs={6}>
            <CentimeterField
              tooltip={{
                text: 'Bestimmt die Dicke der Schicht',
              }}
              label="Querschnittbreite"
              name="layer.crossSectionBottomChord.shape.width"
              data-cy="crosssection-bottom-chord-shape-width"
              twoDecimalPlaces
            />
          </Grid>
          <Grid item xs={6}>
            <CentimeterField
              label="Querschnitthöhe"
              name="layer.crossSectionBottomChord.shape.height"
              data-cy="crosssection-bottom-chord-shape-height"
              twoDecimalPlaces
            />
          </Grid>
          <Grid item xs={6}>
            <Select
              label="Nutzungsklasse"
              name="layer.crossSectionBottomChord.usage_class"
              options={usageClassOptions}
              data-cy="crosssection-bottom-chord-usage-class"
            />
          </Grid>
        </Grid>
      </TabPanel>
    </TabContext>
  )

  const renderRoofCeilingFields = () => (
    <Grid container spacing={2} marginX={-2}>
      <Grid item xs={12}>
        <Select
          label="Standardbalken Materialtyp"
          name="materialTypeStandardBeam"
          options={materialTypeOptions}
          data-cy="select-standard-rip"
        />
      </Grid>
      <Grid item xs={12}>
        <SelectLoadable
          label="Standardbalken Material"
          name="layer.crossSectionStandardBeam.material"
          placeholder="Material auswählen"
          query={getMaterialQueryByType(materialTypeStandardWatch)}
          renderOptions={(data: Material[] = []) =>
            data.map(material => ({
              value: material,
              label: remapMaterialIdentifier(material.identifier),
            }))
          }
          data-cy="crosssection-standard-material"
        />
      </Grid>
      <Grid item xs={6}>
        <CentimeterField
          label="Querschnittbreite"
          name="layer.crossSectionStandardBeam.shape.width"
          data-cy="crosssection-standard-shape-width"
        />
      </Grid>
      <Grid item xs={6}>
        <CentimeterField
          tooltip={{
            text: 'Bestimmt die Dicke der Schicht',
          }}
          label="Querschnitthöhe"
          name="layer.crossSectionStandardBeam.shape.height"
          data-cy="crosssection-standard-shape-height"
        />
      </Grid>
      <Grid item xs={6}>
        <CentimeterField label="Balken Abstand" name="layer.rip_step_size" />
      </Grid>
      <Grid item xs={6}>
        <Select
          label="Nutzungsklasse"
          name="layer.crossSectionStandardBeam.usage_class"
          options={usageClassOptions}
          data-cy="crosssection-standard-usage-class"
        />
      </Grid>
    </Grid>
  )

  return (
    <Box mt={-2}>
      {variant === 'wall' ? renderWallFields() : renderRoofCeilingFields()}

      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Divider />
        </Grid>
        <Grid item xs={12} container justifyContent="center">
          <FormControlLabel
            control={
              <Switch
                checked={hasInsulation}
                onChange={handleInsulationToggle}
                name="layer.hasInsulation"
                data-cy="layer-has-insulation"
              />
            }
            label="Isolierung"
          />
        </Grid>
        {hasInsulation && (
          <>
            <Grid item xs={12}>
              <TextField
                label="Isolierung Name"
                name="layer.insulation.name"
                data-cy="layer-insulation-name"
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Isolierung Dichte"
                name="layer.insulation.density"
                type="number"
                unit="kg/㎥"
                data-cy="layer-insulation-density"
              />
            </Grid>
            <Grid item xs={6}>
              <MillimeterField
                label="Isolierung Breite"
                name="layer.insulation_thickness"
                data-cy="layer-insulation-thickness"
              />
            </Grid>
          </>
        )}
      </Grid>
    </Box>
  )
}

export default TimberFrameLayerFormFields
